import { useEffect, useState, type ReactNode } from "react"
import { Head, Link, usePage } from "@inertiajs/react"
import { FiAlertCircle, FiCheckCircle, FiGrid, FiLogOut, FiShield, FiUsers, FiX } from "react-icons/fi"

type PageProps = {
  flash?: { notice?: string; alert?: string }
  auth?: { logged_in?: boolean; user?: { name: string; username: string; email: string; role?: string; admin: boolean } | null }
}

function FlashToast({ message, kind }: { message: string; kind: "notice" | "alert" }) {
  const [visible, setVisible] = useState(true)
  const isAlert = kind === "alert"

  useEffect(() => {
    const timeout = window.setTimeout(() => setVisible(false), 5000)
    return () => window.clearTimeout(timeout)
  }, [])

  if (!visible) return null

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 rounded-2xl border px-4 py-3 shadow-lg backdrop-blur ${
        isAlert ? "border-rose-200 bg-rose-50/95 text-rose-800" : "border-emerald-200 bg-emerald-50/95 text-emerald-800"
      }`}
      role={isAlert ? "alert" : "status"}
      aria-live={isAlert ? "assertive" : "polite"}
    >
      {isAlert ? <FiAlertCircle className="mt-0.5 shrink-0 text-lg" aria-hidden="true" /> : <FiCheckCircle className="mt-0.5 shrink-0 text-lg" aria-hidden="true" />}
      <p className="min-w-0 flex-1 text-sm font-medium leading-6">{message}</p>
      <button
        type="button"
        onClick={() => setVisible(false)}
        className="-m-1 shrink-0 rounded-lg p-1 transition hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-current/30"
        aria-label="סגירת ההודעה"
      >
        <FiX aria-hidden="true" />
      </button>
    </div>
  )
}

export function AppLayout({ children, title = "Cardly", customerMode = false }: { children: ReactNode; title?: string; customerMode?: boolean }) {
  const { props } = usePage<PageProps>()
  const documentTitle = title === "Cardly" ? title : `${title} | Cardly`
  const isStaff = props.auth?.user?.role === "admin" || props.auth?.user?.role === "superadmin" || props.auth?.user?.admin

  return (
    <>
      <Head title={documentTitle} />
      <div className="page-shell">
      {customerMode ? <header className="border-b border-white/60 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <Link href="/dash" className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-600 text-white shadow-soft">C</span>
            Cardly
          </Link>
          {props.auth?.logged_in ? (
            <nav className="flex items-center gap-2">
              <Link href="/dash" className="btn-secondary px-3 py-2 text-sm">לוח הבקרה</Link>
              <Link href="/logout" method="delete" as="button" className="btn-secondary px-3 py-2 text-sm">התנתקות</Link>
            </nav>
          ) : null}
        </div>
      </header> : null}
      {!customerMode ? <header className="border-b border-white/60 bg-white/70 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <div>
            <div className="flex items-center gap-2 text-lg font-bold text-slate-900"><span className="grid h-9 w-9 place-items-center rounded-xl bg-brand-600 text-white shadow-soft">C</span>Cardly</div>
            <div className="text-sm text-slate-500">קארדי - כרטיס עסק דיגיטלי בעברית</div>
            {props.auth?.user && !customerMode ? (
              <div className="mt-1 text-xs text-slate-400">
                {props.auth.user.admin ? "מנהל מערכת" : `@${props.auth.user.username}`}
              </div>
            ) : null}
          </div>
          <nav className="flex items-center gap-2">
            {props.auth?.logged_in ? (
              <>
                {!customerMode && <Link href={isStaff ? "/admin" : "/dash"} className="btn-secondary gap-2"><FiGrid aria-hidden="true" />{isStaff ? "ניהול מערכת" : "לוח הבקרה"}</Link>}
                {isStaff && !customerMode ? <Link href="/admin/users" className="btn-secondary gap-2"><FiUsers aria-hidden="true" />משתמשים</Link> : null}
                <Link href="/logout" method="delete" as="button" className="btn-secondary gap-2"><FiLogOut aria-hidden="true" />התנתקות</Link>
              </>
            ) : (
              <>
                <Link href="/login" className="btn-secondary gap-2"><FiShield aria-hidden="true" />כניסה</Link>
                <Link href="/register" className="btn-primary gap-2 bg-brand-600"><FiUsers aria-hidden="true" />הרשמה</Link>
              </>
            )}
          </nav>
        </div>
      </header> : null}

      {props.flash?.notice || props.flash?.alert ? (
        <div className="pointer-events-none fixed inset-x-4 top-4 z-50 flex flex-col items-center gap-3 sm:inset-x-auto sm:left-6 sm:right-auto sm:w-[min(calc(100%-3rem),28rem)]">
          {props.flash.notice ? <FlashToast key={`notice-${props.flash.notice}`} message={props.flash.notice} kind="notice" /> : null}
          {props.flash.alert ? <FlashToast key={`alert-${props.flash.alert}`} message={props.flash.alert} kind="alert" /> : null}
        </div>
      ) : null}

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </main>
      </div>
    </>
  )
}
